/**
 * Formatters and helper utilities for shoplaiai
 */

/**
 * Format number or numeric string to Vietnamese Dong currency format (e.g. 350.000 ₫)
 * @param {number|string} amount 
 * @returns {string}
 */
export function formatVND(amount) {
  if (amount === null || amount === undefined || amount === '') return '0 ₫';
  const num = typeof amount === 'number' ? amount : parseInt(String(amount).replace(/[^0-9]/g, ''), 10);
  if (isNaN(num)) return '0 ₫';
  return new Intl.NumberFormat('vi-VN').format(num) + ' ₫';
}

/**
 * Format currency with leading ₫ symbol (e.g. ₫350.000)
 * @param {number|string} amount 
 * @returns {string}
 */
export function formatPriceSymbol(amount) {
  if (amount === null || amount === undefined || amount === '') return '₫0';
  const num = typeof amount === 'number' ? amount : parseInt(String(amount).replace(/[^0-9]/g, ''), 10);
  if (isNaN(num)) return '₫0';
  return '₫' + new Intl.NumberFormat('vi-VN').format(num);
}

/**
 * Format compact price for sliders and badges (e.g. 500k ₫, 1.5M ₫)
 * @param {number} amount 
 * @returns {string}
 */
export function formatPriceCompact(amount) {
  if (!amount || amount <= 0) return '0 ₫';
  if (amount >= 1000000) {
    const m = (amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1);
    return `${m}M ₫`;
  }
  if (amount >= 1000) {
    const k = Math.round(amount / 1000);
    return `${k}k ₫`;
  }
  return `${amount} ₫`;
}

/**
 * Parse any price string or number into an integer VND value
 * @param {string|number} price 
 * @returns {number}
 */
export function parsePrice(price) {
  if (typeof price === 'number') return Math.max(0, Math.floor(price));
  if (!price) return 0;
  const digits = String(price).replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) : 0;
}

/**
 * Format date string into human-readable relative time (e.g. "5m ago", "2h ago", "yesterday")
 * @param {string|Date} dateStr 
 * @returns {string}
 */
export function formatRelativeTime(dateStr) {
  if (!dateStr) return 'Never checked';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSec < 0 || diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDays = Math.floor(diffHour / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' });
  } catch {
    return 'Recently';
  }
}

/**
 * Detect platform ('shopee' | 'tiktok' | 'other') from URL string
 * @param {string} url 
 * @returns {'shopee' | 'tiktok' | 'other'}
 */
export function detectPlatform(url) {
  if (!url || typeof url !== 'string') return 'other';
  const lower = url.toLowerCase();
  if (lower.includes('shopee.vn') || lower.includes('shope.ee') || lower.includes('s.shopee.vn')) {
    return 'shopee';
  }
  if (lower.includes('tiktok.com') || lower.includes('vt.tiktok.com') || lower.includes('shop.tiktok.com')) {
    return 'tiktok';
  }
  return 'other';
}

/**
 * Truncate long text with ellipsis
 * @param {string} text 
 * @param {number} maxLength 
 * @returns {string}
 */
export function truncateText(text, maxLength = 60) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Humanize grey reason into user-facing label
 * @param {string} reason 
 * @returns {{ label: string, color: string, badgeBg: string }}
 */
export function formatGreyReason(reason) {
  switch (reason) {
    case 'out_of_stock':
      return { label: 'Hết hàng / Out of Stock', color: 'text-amber-400', badgeBg: 'bg-amber-500/20 border-amber-500/30' };
    case 'shop_closed':
      return { label: 'Shop tạm nghỉ / Shop Closed', color: 'text-red-400', badgeBg: 'bg-red-500/20 border-red-500/30' };
    case 'broken_link':
    case 'not_found':
      return { label: 'Link hỏng / 404 Not Found', color: 'text-purple-400', badgeBg: 'bg-purple-500/20 border-purple-500/30' };
    case 'error':
    default:
      return { label: 'Lỗi kết nối / Error', color: 'text-zinc-400', badgeBg: 'bg-zinc-500/20 border-zinc-500/30' };
  }
}

/**
 * Category translation and icon map
 */
export const CATEGORY_MAP = {
  all: { name: 'Tất cả bao lì xì', icon: '✨', iconName: 'sparkles' },
  fashion: { name: 'Thời Trang & May Mặc', icon: '👕', iconName: 'shirt' },
  beauty: { name: 'Làm Đẹp & Chăm Sóc Da', icon: '💄', iconName: 'sparkles' },
  electronics: { name: 'Điện Tử & Công Nghệ', icon: '💻', iconName: 'laptop' },
  home: { name: 'Nhà Cửa & Đời Sống', icon: '🏠', iconName: 'home' },
  accessories: { name: 'Phụ Kiện & Đồng Hồ', icon: '⌚', iconName: 'watch' },
  food: { name: 'Ẩm Thực & Ăn Vặt', icon: '☕', iconName: 'coffee' },
  health: { name: 'Sức Khỏe & Đề Kháng', icon: '❤️', iconName: 'heart' }
};

/**
 * Get localized Vietnamese name and icon for any category slug or name
 * @param {string} slugOrName 
 * @returns {{ name: string, icon: string, iconName: string }}
 */
export function getCategoryInfo(slugOrName) {
  if (!slugOrName) return { name: 'Ưu Đãi Đặc Biệt', icon: '🧧', iconName: 'folder' };
  const key = String(slugOrName).toLowerCase();
  for (const [slug, item] of Object.entries(CATEGORY_MAP)) {
    if (key === slug || key === item.name.toLowerCase()) return item;
    if (key.includes('fashion') || key.includes('thoi trang')) return CATEGORY_MAP.fashion;
    if (key.includes('beauty') || key.includes('skincare') || key.includes('lam dep') || key.includes('my pham')) return CATEGORY_MAP.beauty;
    if (key.includes('electronic') || key.includes('dien tu') || key.includes('cong nghe')) return CATEGORY_MAP.electronics;
    if (key.includes('home') || key.includes('nha cua') || key.includes('lifestyle') || key.includes('doi song')) return CATEGORY_MAP.home;
    if (key.includes('accessori') || key.includes('phu kien') || key.includes('dong ho')) return CATEGORY_MAP.accessories;
    if (key.includes('food') || key.includes('beverage') || key.includes('am thuc') || key.includes('an vat')) return CATEGORY_MAP.food;
    if (key.includes('health') || key.includes('supplement') || key.includes('suc khoe') || key.includes('de khang')) return CATEGORY_MAP.health;
  }
  return { name: slugOrName, icon: '🧧', iconName: 'folder' };
}
